"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoClient = void 0;
const tslib_1 = require("tslib");
const log_client_1 = require("@video/log-client");
const mobx_1 = require("mobx");
const adapter_1 = require("../api/adapter");
const manifest_1 = require("../api/manifest");
const package_json_1 = tslib_1.__importDefault(require("../package-json"));
const common_1 = require("../utils/common");
const player_1 = require("../v3/player/player");
const call_1 = require("./call");
const errors_1 = require("./errors");
const errors_deprecated_1 = require("./errors-deprecated");
const media_controller_1 = require("./media-controller");
const media_loader_1 = tslib_1.__importDefault(require("./media-loader"));
const mediasoup_source_1 = require("./mediasoup-source");
const manifest_2 = require("./player/manifest");
const mediasoup_1 = require("./player/mediasoup");
const bind_1 = require("./utils/bind");
const browser_support_1 = require("./utils/browser-support");
const context_1 = require("./utils/context/context");
const event_emitter_1 = require("./utils/events/event-emitter");
const logger_1 = require("./utils/logger");
const events_handler_1 = require("./utils/proxy/events-handler");
const support_1 = require("./utils/support");
const defaultPlayerOptions = {
    autoPlay: true,
    refetch: false,
    displayPoster: "preview",
    players: [{ id: "webrtc" }, { id: "native-hls" }, { id: "hlsjs" }],
    muted: false,
    volume: 0.5,
    driverFailoverSeconds: 10,
    pollingInterval: 5000,
    unauthorizedPollingInterval: 2000,
    notFoundPollingInterval: 10000,
    unauthorizedRecoveryDuration: 10000,
    retryCall: true,
    urlParams: {
        vdc: true,
        substitute: false,
    },
};
const proxyEventsHandler = new events_handler_1.EventsHandler();
/**
 * Entry point class for conferences and cam2cam activities
 */
class VideoClient extends event_emitter_1.ObservableEventEmitter {
    /**
     * Main endpoint to work with video-client
     *
     * @throws TypeGuardError if options are not correct. See VideoClientOptions
     */
    constructor(options) {
        var _a, _b, _c, _d, _e, _f;
        super(false);
        this.options = { backendEndpoints: [], userId: "" };
        this.setMaxListeners(20);
        const logger = (_a = options === null || options === void 0 ? void 0 : options.logger) !== null && _a !== void 0 ? _a : new log_client_1.LoggerCore("VDC-core");
        options === null || options === void 0 ? true : delete options.logger;
        const client = (_c = (_b = options === null || options === void 0 ? void 0 : options.loggerConfig) === null || _b === void 0 ? void 0 : _b.clientName) !== null && _c !== void 0 ? _c : "VDC";
        const prevClient = logger.getLoggerMeta("client");
        const prevRelease = logger.getLoggerMeta("release");
        const prevPackage = logger.getLoggerMeta("package");
        const connection = adapter_1.device.isImplements(adapter_1.Feature.NETWORK_INFORMATION)
            ? (_d = adapter_1.device === null || adapter_1.device === void 0 ? void 0 : adapter_1.device.connection) === null || _d === void 0 ? void 0 : _d.type
            : "information unavailable";
        const [connectionType, connectionEffectiveType] = (0, browser_support_1.getNetworkConnectionType)();
        logger
            .setLoggerMeta("package", prevPackage != null && prevPackage !== "VDC-core" ? `${prevPackage}/VDC-core` : "VDC-core")
            .setLoggerMeta("client", prevClient != null ? `${prevClient}/${client}` : client)
            .setLoggerMeta("release", prevRelease != null ? `${prevRelease}/${package_json_1.default.version}` : package_json_1.default.version)
            .setLoggerMeta("commitHash", package_json_1.default.commit)
            .setLoggerMeta("contextId", (_e = (0, common_1.contextId)()) !== null && _e !== void 0 ? _e : "")
            .setLoggerMeta("instanceId", (_f = (0, common_1.instanceId)()) !== null && _f !== void 0 ? _f : "")
            .appendChain(VideoClient)
            .setMessageAggregate("displayName", options === null || options === void 0 ? void 0 : options.displayName)
            .setMessageAggregate("networkConnection", connection)
            .setMessageAggregate("networkConnectionType", connectionType)
            .setMessageAggregate("networkConnectionEffectiveType", connectionEffectiveType);
        const support = new support_1.Support(new log_client_1.LoggerCore("VDC-core").extend(logger).appendChain(support_1.Support));
        if (adapter_1.device.isIE) {
            this.emitError((0, errors_1.createError)(errors_1.ErrorCode.IENotSupported, "IE is not supported", {}));
        }
        this.ctx = (0, context_1.context)({
            logger,
            videoClient: this,
            support,
            chain: VideoClient.displayName,
            token: options.token,
        });
        (0, mobx_1.makeObservable)(this, {
            requestPlayer: mobx_1.action,
        });
        const self = (0, bind_1.makeBounded)(new Proxy(this, proxyEventsHandler));
        self.init(options);
        logger.trace("constructor()", {
            backendEndpoints: options === null || options === void 0 ? void 0 : options.backendEndpoints,
        });
        return self;
    }
    init(options) {
        var _a;
        // For backwards compatibility. VideoClient.autoPlay should be deprecated.
        // @todo move to unified option validation (e.g. zog)
        if ((options === null || options === void 0 ? void 0 : options.autoPlay) != null && (options === null || options === void 0 ? void 0 : options.playerOptions) != null && ((_a = options === null || options === void 0 ? void 0 : options.playerOptions) === null || _a === void 0 ? void 0 : _a.autoPlay) == null) {
            options.playerOptions.autoPlay = options.autoPlay;
        }
        this.updateOptions(options);
        // @todo must be removed. implementer should handle errors
        this.on("error", (err) => {
            errors_1.VideoClientError.log(err, this.ctx.logger);
        });
    }
    /**
     * @throws VideoClientValidationError if options is not correct VideoClientOptions
     */
    static validateOptions(videoClientOptions) {
        return videoClientOptions;
    }
    /**
     * Updates current options
     *
     * @throws TypeGuardError if options is not correct VideoClientOptions
     */
    updateOptions(options) {
        if (options == null) {
            return;
        }
        const validatedOpts = VideoClient.validateOptions(options);
        if (validatedOpts.logger != null) {
            this.ctx.logger.warn("using `logger` in updateOptions() is not allowed");
            delete validatedOpts.logger;
        }
        this.options = { ...this.options, ...validatedOpts };
    }
    /**
     * Returns true if Video Client with provided options supports
     * a creating or joining calls
     */
    async callSupported() {
        await this.ctx.support.ready;
        return this.ctx.support.supports("h264") || this.ctx.support.supports("vp8");
    }
    /**
     * Creates and establishes a connection to a new call
     * Returns Call which is ready to use (e.g. create broadcasts)
     * but doesn't have any peers yet
     *
     * @throws {H264NotSupported} if h264 is not supported
     * @throws {NoBackendEndpoints} if backendEndpoints is not provided
     */
    async createCall(callOptions) {
        var _a;
        await this.ctx.support.ready;
        const { backendEndpoints, token } = this.options;
        await this.validateCallOptions();
        const isOwner = true;
        const callOptionsParams = {
            token,
            backendEndpoints,
            sfu: { ...((_a = callOptions.sfu) !== null && _a !== void 0 ? _a : {}) },
            playerOptions: this.options.playerOptions,
            clientReferrer: callOptions.clientReferrer,
            streamKey: callOptions.streamKey,
        };
        const sfuJoinParams = this.createSFUJoinParams();
        const call = new call_1.Call((0, logger_1.extendContext)(this.ctx, call_1.Call), isOwner, callOptionsParams, sfuJoinParams);
        this[events_handler_1.addProxy](call);
        return call.ready();
    }
    async supportCheck() {
        return this.ctx.support.supportChecks();
    }
    /**
     * Joins to an existing connection
     * Returns Call which is ready to use (e.g. create broadcasts)
     * and already have peers
     *
     * @throws {H264NotSupported} if h264 is not supported
     * @throws {NoBackendEndpoints} if backendEndpoints is not provided
     */
    async joinCall(callId, options = {}) {
        const sfuJoinParams = this.createSFUJoinParams();
        return this.internalJoinCall(callId, sfuJoinParams, options, this.ctx);
    }
    async internalJoinCall(callId, sfuJoinParams, options = {}, ctx = this.ctx) {
        const { playerOptions, token, ...sfu } = options;
        const { backendEndpoints, displayName, userId } = this.options;
        const { onCallJoinEndpoint, onSFUConnection } = options;
        await this.validateCallOptions();
        const isOwner = false;
        const callOptionsParams = {
            token: token !== null && token !== void 0 ? token : this.options.token,
            backendEndpoints,
            callId,
            sfu,
            playerOptions: playerOptions !== null && playerOptions !== void 0 ? playerOptions : this.options.playerOptions,
        };
        const callSFUJoinParams = {
            ...sfuJoinParams,
            user: {
                displayName: displayName !== null && displayName !== void 0 ? displayName : userId,
                userId,
                ...sfuJoinParams === null || sfuJoinParams === void 0 ? void 0 : sfuJoinParams.user,
            },
        };
        const call = new call_1.Call((0, logger_1.extendContext)(ctx, call_1.Call), isOwner, callOptionsParams, callSFUJoinParams);
        call.on("callJoinEndpoint", () => {
            if (onCallJoinEndpoint) {
                onCallJoinEndpoint();
            }
        });
        call.on("sfuConnection", () => {
            if (onSFUConnection) {
                onSFUConnection();
            }
        });
        this[events_handler_1.addProxy](call);
        return call.ready();
    }
    /**
     * @throws {NoPlayersAvailable} if no players available by any reason
     */
    requestPlayer(source, options = defaultPlayerOptions) {
        var _a;
        const playerOptions = {
            autoPlay: options.autoPlay != null ? options.autoPlay : defaultPlayerOptions.autoPlay,
            displayPoster: options.displayPoster != null ? options.displayPoster : defaultPlayerOptions.displayPoster,
            players: options.players != null ? options.players : defaultPlayerOptions.players,
            refetch: options.refetch != null ? options.refetch : defaultPlayerOptions.refetch,
            muted: options.muted != null ? options.muted : defaultPlayerOptions.muted,
            pollingInterval: options.pollingInterval != null ? options.pollingInterval : defaultPlayerOptions.pollingInterval,
            unauthorizedPollingInterval: options.unauthorizedPollingInterval != null
                ? options.unauthorizedPollingInterval
                : defaultPlayerOptions.unauthorizedPollingInterval,
            notFoundPollingInterval: options.notFoundPollingInterval != null
                ? options.notFoundPollingInterval
                : defaultPlayerOptions.notFoundPollingInterval,
            unauthorizedRecoveryDuration: options.unauthorizedRecoveryDuration != null
                ? options.unauthorizedRecoveryDuration
                : defaultPlayerOptions.unauthorizedRecoveryDuration,
            volume: options.volume != null ? options.volume : defaultPlayerOptions.volume,
            driverFailoverSeconds: options.driverFailoverSeconds != null
                ? options.driverFailoverSeconds
                : defaultPlayerOptions.driverFailoverSeconds,
            retryCall: options.retryCall != null ? options.retryCall : defaultPlayerOptions.retryCall,
            urlParams: options.urlParams != null ? options.urlParams : defaultPlayerOptions.urlParams,
            requestHeaders: options.requestHeaders != null ? options.requestHeaders : defaultPlayerOptions.requestHeaders,
            experimental: (_a = options.experimental) !== null && _a !== void 0 ? _a : false,
        };
        // For some reason logger is being attached to the options which is breaking this log.
        const loggerOptions = {
            muted: options.muted,
            autoPlay: options.autoPlay,
            refetch: options.refetch,
            volume: options.volume,
        };
        this.ctx.logger.debug("requestPlayer()", {
            manifest: source,
            mergedPlayerOptions: JSON.stringify(playerOptions),
            options: loggerOptions,
        });
        const inner = null;
        let player = null;
        // try {
        player = this.internalRequestPlayer(source, playerOptions);
        // } catch (ex) {
        //   if (ex instanceof Error) {
        //     inner = ex;
        //   }
        // }
        if (player == null) {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.NoPlayersAvailable, "No supported players", {}, inner));
        }
        this[events_handler_1.addProxy](player);
        return player;
    }
    createSFUJoinParams() {
        const { displayName, userId } = this.options;
        return {
            user: {
                displayName: displayName !== null && displayName !== void 0 ? displayName : userId,
                userId,
            },
        };
    }
    async validateCallOptions() {
        const { backendEndpoints, userId } = this.options;
        if (!(await this.callSupported())) {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.H264NotSupported, "h264 is not supported", {}));
        }
        if (backendEndpoints == null) {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.NoBackendEndpoints, "backendEndpoints is not provided", {}));
        }
        if (userId == null) {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.NoUserId, "userId is not provided", {}));
        }
    }
    internalRequestPlayer(source, playerOptions) {
        if (playerOptions.experimental) {
            if (typeof source !== "string") {
                throw new Error("source must be URL");
            }
            return (0, player_1.requestPlayer)((0, logger_1.extendContext)(this.ctx, player_1.Player), source, playerOptions);
        }
        if ((0, manifest_1.isManifest)(source)) {
            const manifestPlayerCtx = (0, logger_1.extendContext)(this.ctx, manifest_2.ManifestPlayer);
            const mediaLoader = new media_loader_1.default((0, logger_1.extendContext)(manifestPlayerCtx, media_loader_1.default), source, {
                pollingInterval: playerOptions.pollingInterval,
                notFoundPollingInterval: playerOptions.notFoundPollingInterval,
                unauthorizedPollingInterval: playerOptions.unauthorizedPollingInterval,
                unauthorizedRecoveryDuration: playerOptions.unauthorizedRecoveryDuration,
                urlParams: playerOptions.urlParams,
                requestHeaders: playerOptions.requestHeaders,
            });
            mediaLoader.on("error", (err) => {
                this.emit("error", err);
            });
            // Remove mediasoup player as an option
            const filteredPlayers = playerOptions.players;
            const defaultPlayerSpecOptions = {
                autoPlay: playerOptions.autoPlay,
                muted: playerOptions.muted,
                volume: playerOptions.volume,
                blurred: playerOptions.blurred,
                retryCall: playerOptions.retryCall,
                requestHeaders: playerOptions.requestHeaders,
            };
            // Set player options
            filteredPlayers.forEach((item) => {
                if (typeof item !== "string") {
                    item.options = { ...defaultPlayerSpecOptions, ...item.options };
                }
            });
            const player = new manifest_2.ManifestPlayer(manifestPlayerCtx, mediaLoader, {
                ...playerOptions,
                players: filteredPlayers,
            });
            if (playerOptions.preferredScoreLevel != null) {
                player.preferredLevel = playerOptions.preferredScoreLevel;
            }
            return player;
        }
        if (source instanceof mediasoup_source_1.MediasoupSource) {
            return new mediasoup_1.MediasoupPlayer((0, logger_1.extendContext)(this.ctx, mediasoup_1.MediasoupPlayer), source, {});
        }
        if (source instanceof media_controller_1.MediaStreamController) {
            return new mediasoup_1.MediasoupPlayer((0, logger_1.extendContext)(this.ctx, mediasoup_1.MediasoupPlayer), source, {});
        }
        throw new errors_deprecated_1.NotSupportedError("source not supported", {});
    }
}
exports.VideoClient = VideoClient;
VideoClient.displayName = "VideoClient";
//# sourceMappingURL=video-client.js.map